import React, { Component } from "react";
import { connect } from "react-redux";
import axios from "axios";
import { arrayMove } from "react-sortable-hoc";

// Components
import GeneralLoader from "../../GeneralLoader";
import ErrorText from "../../utils/error-success/ErrorText";
import ErrorDialog from "../../utils/dialogs/ErrorDialog";
import DeleteConfirmationDialog from "../../utils/dialogs/DeleteConfirmationDialog";
import AddNewTrainingDocumentGroupDialog from "../../utils/dialogs/AddNewTrainingDocumentGroupDialog";
import BlackButton from "../../utils/BlackButton";

// State
import { addRoute } from "../../../store/actions/route";
import { getTrainingDocs } from "../../../store/actions/trainingDocs";
import { userTokenUpdate } from "../../../store/actions/user";
import { trainingDocsSuccess } from "../../../store/actions/trainingDocs";

// Utils
import SortableGroupList from "./SortableGroupList";

class AdminTrainingDocuments extends Component {
	constructor(props) {
		super(props);

		this.props.addRoute(this.props.match);

		this.state = {
			fetched: false,
			data: {},
			showErrorDialog: false,
			showDeleteConfirmationDialog: false,
			showNewGroupDialog: false,
			deleteItem: {},
			minimizeAllGroups: false
		};
	}

	static initialAction(store, props, translationKey = null) {
		const promises = [];

		let resDocs = store !== null ? getTrainingDocs(true) : props.getTrainingDocs(true);
		promises.push(resDocs);

		return promises;
	}

	static getDerivedStateFromProps(props, state) {
		// If the store documents exist and has not been fetched yet
		if (!!props.documents && !state.fetched) {
			return {
				fetched: true,
				data: {
					...state.data,
					documents: props.documents
				}
			};
		}

		return state;
	}

	/**
	 *
	 * This function manually grabs the documents from the store.
	 * Without it, it's a lot of workarounds to make it work with getDerivedStateFromProps()
	 *
	 */
	updateLocalStateFromProps = (groups = null) => {
		if (groups === null) {
			groups = this.props.documents;
		}

		this.setState({
			data: {
				...this.state.data,
				documents: [...groups]
			}
		});
	};

	// ===================================
	//  LIFE CYCLE
	// ===================================
	componentDidMount() {
		// Grab documents if not already in store
		// if (!this.props.documents) {
		AdminTrainingDocuments.initialAction(null, this.props);
		// }
	}

	// ===================================
	//  INPUT HANDLING
	// ===================================
	minimizeAll = () => {
		this.setState({
			data: {
				...this.state.data,
				documents: [
					...this.state.data.documents.map(group => {
						return { ...group, minimize: !this.state.minimizeAllGroups };
					})
				]
			},
			minimizeAllGroups: !this.state.minimizeAllGroups
		});
	};

	changeGroupMinify = group => {
		this.setState({
			data: {
				...this.state.data,
				documents: [
					...this.state.data.documents.map(item => {
						if (item.id !== group.id) {
							return item;
						}

						return {
							...item,
							minimize: !!item.minimize ? !item.minimize : true
						};
					})
				]
			}
		});
	};

	handleTitleChange = (e, index) => {
		const { value } = e.target;

		// Update the title value of the document object
		const newDocs = this.state.data.documents.map((item, i) => {
			if (i !== index) {
				return item;
			}

			return {
				...item,
				title: value,
				titleChanged: true
			};
		});

		this.setState({
			data: {
				...this.state.data,
				documents: [...newDocs]
			}
		});
	};

	updateFileTitle = (e, fileId, group) => {
		const { value } = e.target;

		// Update the title value of the document object
		const newDocs = this.state.data.documents.map(item => {
			if (item.id !== group.id) {
				return item;
			}

			// This is the group.  Let's find the correct file to update
			return {
				...item,
				files: item.files.map(file => {
					if (fileId !== file.id) {
						return file;
					}

					// Update the title, and set the "changed" status
					return {
						...file,
						title: value,
						titleChanged: true
					};
				})
			};
		});

		// Finally, set state
		this.setState({
			data: {
				...this.state.data,
				documents: [...newDocs]
			}
		});
	};

	// ===================================
	//  GROUP UPDATES
	// ===================================
	addNewGroup = title => {
		this.setState(
			{
				newGroupInProgress: true,
				newGroupError: null
			},
			() => {
				const { user } = this.props;

				const data = {
					title,
					language: process.env.REACT_APP_LANGUAGE
				};

				const token = user.token;
				const AUTH_HEADER = {
					Authorization: process.env.REACT_APP_SITE_KEY + ":Bearer " + token
				};
				return axios
					.post(process.env.REACT_APP_API_URL + "training-docs/add-group", data, {
						headers: AUTH_HEADER
					})
					.then(response => {
						if (!!response.data.token) {
							this.props.userTokenUpdate("admin", response.data.token);
						}

						// ===================================
						//  ERROR
						// ===================================
						if (
							response.status !== 200 ||
							(response.data.status && response.data.status !== 200)
						) {
							this.setState({
								newGroupInProgress: false,
								newGroupError: response.data.errors
							});
						}
						// ===================================
						//  SUCCESS
						// ===================================
						else {
							// Update documents in store
							this.props.trainingDocsSuccess(response.data.groups);

							// Hide "new group" dialog, reset any flags, and add the new documents item
							this.setState(
								{
									newGroupInProgress: false,
									showNewGroupDialog: false
								},
								() => {
									// Supply new docs to local state
									this.updateLocalStateFromProps(response.data.groups);
								}
							);
						}
					})
					.catch(err => {
						this.setState({
							newGroupInProgress: false,
							newGroupError: err.toString()
						});
					});
			}
		);
	};

	saveGroupTitle = group => {
		let groupIndex = null;

		// Set state of saving group
		const savedDocState = this.state.data.documents.map((item, i) => {
			if (item.id !== group.id) {
				return item;
			}

			groupIndex = i;

			return { ...item, saving: true, successful: false };
		});

		this.setState(
			{
				data: {
					...this.state.data,
					documents: [...savedDocState]
				}
			},
			() => {
				const { user } = this.props;

				const data = {
					...group,
					language: process.env.REACT_APP_LANGUAGE
				};

				const token = user.token;
				const AUTH_HEADER = {
					Authorization: process.env.REACT_APP_SITE_KEY + ":Bearer " + token
				};
				return axios
					.post(process.env.REACT_APP_API_URL + "training-docs/title", data, {
						headers: AUTH_HEADER
					})
					.then(response => {
						if (!!response.data.token) {
							this.props.userTokenUpdate("admin", response.data.token);
						}

						// ===================================
						//  ERROR
						// ===================================
						if (
							response.status !== 200 ||
							(response.data.status && response.data.status !== 200)
						) {
							this.setState({
								showErrorDialog: true,
								errors: response.data.errors
							});

							this.resetGroupSaving(groupIndex);
						}
						// ===================================
						//  SUCCESS
						// ===================================
						else {
							// Reset saving status for group
							this.resetGroupSaving(groupIndex, true);
						}
					})
					.catch(err => {
						this.setState({
							showErrorDialog: true,
							errors: err.toString()
						});
						this.resetGroupSaving(groupIndex);
					});
			}
		);
	};

	resetGroupSaving = (groupIndex, successful = false) => {
		this.setState({
			data: {
				...this.state.data,
				documents: this.state.data.documents.map((doc, i) => {
					if (i !== groupIndex) {
						return doc;
					}

					return {
						...doc,
						saving: false,
						deleting: false,
						successful,
						titleChanged: !successful
					};
				})
			}
		});
	};

	deleteGroup = groupId => {
		let groupIndex = null;

		// Set state of saving group
		const savedDocState = this.state.data.documents.map((item, i) => {
			if (item.id !== groupId) {
				return item;
			}

			groupIndex = i;

			return { ...item, deleting: true };
		});

		this.setState(
			{
				data: {
					...this.state.data,
					documents: [...savedDocState]
				},
				deleteItem: {
					...this.state.deleteItem,
					deletionInProgress: true
				}
			},
			() => {
				const { user } = this.props;

				const token = user.token;
				const AUTH_HEADER = {
					Authorization: process.env.REACT_APP_SITE_KEY + ":Bearer " + token
				};
				return axios
					.delete(
						process.env.REACT_APP_API_URL +
							`training-docs/group/${groupId}?language=${
								process.env.REACT_APP_LANGUAGE
							}`,
						{
							headers: AUTH_HEADER
						}
					)
					.then(response => {
						if (!!response.data.token) {
							this.props.userTokenUpdate("admin", response.data.token);
						}

						// ===================================
						//  ERROR
						// ===================================
						if (
							response.status !== 200 ||
							(response.data.status && response.data.status !== 200)
						) {
							this.setState({
								deleteItem: {
									...this.state.deleteItem,
									deletionInProgress: false,
									deleteErrors: {
										...response.data.errors
									}
								}
							});

							this.resetGroupSaving(groupIndex);
						}
						// ===================================
						//  SUCCESS
						// ===================================
						else {
							// Update documents in store
							this.props.trainingDocsSuccess(response.data.groups);

							// Remove deleted group from local state
							// Also set success message and reset deletion stuffs
							this.setState({
								data: {
									...this.state.data,
									documents: [
										...this.state.data.documents.filter(
											item => item.id !== groupId
										)
									]
								},
								deleteSuccess: response.data.message,
								deleteItem: {}
							});
						}
					})
					.catch(err => {
						this.setState({
							deleteItem: {
								...this.state.deleteItem,
								deletionInProgress: false,
								deleteErrors: err.toString()
							}
						});
						this.resetGroupSaving(groupIndex);
					});
			}
		);
	};

	/**
	 *
	 * We have to accept an object as the arguments so we can easily pass it in from a key handler, etc.
	 * .apply() doesn't work super well.  Maybe it does, but it's lame to use for every other use case.
	 *
	 */
	saveFileTitle = args => {
		const { fileIndex, group } = args;

		// Set state of saving group
		const savedDocState = this.state.data.documents.map(item => {
			if (item.id !== group.id) {
				return item;
			}

			// This is the group.  Let's find the correct file to update
			return {
				...item,
				files: item.files.map((file, k) => {
					if (fileIndex !== k) {
						return file;
					}

					// Update the title, and set the "changed" status
					return { ...file, saving: true, successful: false };
				})
			};
		});

		this.setState(
			{
				data: {
					...this.state.data,
					documents: [...savedDocState]
				}
			},
			() => {
				const { user } = this.props;

				const data = {
					...group,
					fileIndex,
					language: process.env.REACT_APP_LANGUAGE
				};

				const token = user.token;
				const AUTH_HEADER = {
					Authorization: process.env.REACT_APP_SITE_KEY + ":Bearer " + token
				};
				return axios
					.post(process.env.REACT_APP_API_URL + "training-docs/file", data, {
						headers: AUTH_HEADER
					})
					.then(response => {
						if (!!response.data.token) {
							this.props.userTokenUpdate("admin", response.data.token);
						}

						// ===================================
						//  ERROR
						// ===================================
						if (
							response.status !== 200 ||
							(response.data.status && response.data.status !== 200)
						) {
							this.setState({
								showErrorDialog: true,
								errors: response.data.errors
							});

							this.resetFileSaving(group, fileIndex);
						}
						// ===================================
						//  SUCCESS
						// ===================================
						else {
							// Reset saving status for group
							this.resetFileSaving(group, fileIndex, true);
						}
					})
					.catch(err => {
						this.setState({
							showErrorDialog: true,
							errors: err.toString()
						});
						this.resetFileSaving(group, fileIndex);
					});
			}
		);
	};

	resetFileSaving = (group, fileIndex, successful = false) => {
		this.setState({
			data: {
				...this.state.data,
				documents: this.state.data.documents.map(item => {
					if (item.id !== group.id) {
						return item;
					}

					// This is the group.  Let's find the correct file to update
					return {
						...item,
						files: item.files.map((file, k) => {
							if (fileIndex !== k) {
								return file;
							}

							fileIndex = k;

							// Update the title, and set the "changed" status
							return {
								...file,
								saving: false,
								successful,
								titleChanged: !successful
							};
						})
					};
				})
			}
		});
	};

	deleteFile = fileId => {
		this.setState(
			{
				deleteItem: {
					...this.state.deleteItem,
					deletionInProgress: true
				}
			},
			() => {
				const { user } = this.props;

				const token = user.token;
				const AUTH_HEADER = {
					Authorization: process.env.REACT_APP_SITE_KEY + ":Bearer " + token
				};
				return axios
					.delete(
						process.env.REACT_APP_API_URL +
							`training-docs/file/${fileId}?language=${
								process.env.REACT_APP_LANGUAGE
							}`,
						{
							headers: AUTH_HEADER
						}
					)
					.then(response => {
						if (!!response.data.token) {
							this.props.userTokenUpdate("admin", response.data.token);
						}

						// ===================================
						//  ERROR
						// ===================================
						if (
							response.status !== 200 ||
							(response.data.status && response.data.status !== 200)
						) {
							this.setState({
								deleteItem: {
									...this.state.deleteItem,
									deletionInProgress: false,
									deleteErrors: { ...response.data.errors }
								}
							});
						}
						// ===================================
						//  SUCCESS
						// ===================================
						else {
							this.setState(
								{
									deleteSuccess: response.data.message,
									deleteItem: {}
								},
								() => {
									// Update documents in store
									this.props.trainingDocsSuccess(response.data.groups);

									// Supply new docs to local state
									this.updateLocalStateFromProps(response.data.groups);
								}
							);
						}
					})
					.catch(err => {
						this.setState({
							deleteItem: {
								...this.state.deleteItem,
								deletionInProgress: false,
								deleteErrors: err.toString()
							}
						});
					});
			}
		);
	};

	// ===================================
	//  SORT
	// ===================================
	onSortEndGroups = (oldIndex, newIndex) => {
		let newDocs = arrayMove(this.state.data.documents, oldIndex, newIndex);

		newDocs = newDocs.map(group => {
			return {
				...group,
				error: null
			};
		});

		this.setState(
			{
				data: {
					...this.state.data,
					documents: [...newDocs]
				}
			},
			() => {
				const { user } = this.props;

				const data = {
					groups: this.state.data.documents,
					language: process.env.REACT_APP_LANGUAGE
				};

				const token = user.token;
				const AUTH_HEADER = {
					Authorization: process.env.REACT_APP_SITE_KEY + ":Bearer " + token
				};
				return axios
					.post(process.env.REACT_APP_API_URL + `training-docs/group-sort`, data, {
						headers: AUTH_HEADER
					})
					.then(response => {
						if (!!response.data.token) {
							this.props.userTokenUpdate("admin", response.data.token);
						}

						// ===================================
						//  ERROR
						// ===================================
						if (
							response.status !== 200 ||
							(response.data.status && response.data.status !== 200)
						) {
							this.setState({
								data: {
									...this.state.data,
									documents: [
										...this.state.data.documents.map((group, i) => {
											if (i !== newIndex) {
												return group;
											}

											return {
												...group,
												error: response.data.errors
											};
										})
									]
								}
							});
						}
					})
					.catch(err => {
						this.setState({
							data: {
								...this.state.data,
								documents: [
									...this.state.data.documents.map((group, i) => {
										if (i !== newIndex) {
											return group;
										}

										return {
											...group,
											error: err
										};
									})
								]
							}
						});
					});
			}
		);
	};

	onSortEndFiles = (oldIndex, newIndex, groupIndex) => {
		const newDocs = this.state.data.documents.map((group, i) => {
			if (groupIndex !== i) {
				return {
					...group,
					error: null
				};
			}

			let files = group.files;

			return {
				...group,
				files: arrayMove(files, oldIndex, newIndex),
				error: null
			};
		});

		this.setState(
			{
				data: {
					...this.state.data,
					documents: [...newDocs]
				}
			},
			() => {
				const { user } = this.props;

				const data = {
					group_id: newDocs[groupIndex].id,
					files: newDocs[groupIndex].files,
					language: process.env.REACT_APP_LANGUAGE
				};

				const token = user.token;
				const AUTH_HEADER = {
					Authorization: process.env.REACT_APP_SITE_KEY + ":Bearer " + token
				};
				return axios
					.post(process.env.REACT_APP_API_URL + `training-docs/document-sort`, data, {
						headers: AUTH_HEADER
					})
					.then(response => {
						if (!!response.data.token) {
							this.props.userTokenUpdate("admin", response.data.token);
						}

						// ===================================
						//  ERROR
						// ===================================
						if (
							response.status !== 200 ||
							(response.data.status && response.data.status !== 200)
						) {
							newDocs[groupIndex].error = response.data.errors;

							this.setState({
								data: {
									...this.state.data,
									documents: [...newDocs]
								}
							});
						}
						// ===================================
						//  SUCCESS
						// ===================================
						else {
							this.setState({
								...this.state.data,
								documents: [...newDocs]
							});
						}
					})
					.catch(err => {
						newDocs[groupIndex].error = err;

						this.setState({
							data: {
								...this.state.data,
								documents: [...newDocs]
							}
						});
					});
			}
		);
	};

	// ===================================
	//  RENDER
	// ===================================
	render() {
		const { isLoading, docErrors } = this.props;
		const { documents } = this.state.data;

		return (
			<div>
				<div className="linked-title">
					<h1>Edit Training Documents</h1>
					<div className="btn_container">
						{!!documents && !!documents.length && (
							<BlackButton className="btn" clickHandler={this.minimizeAll}>
								{!!this.state.minimizeAllGroups ? "Expand All" : "Minimize All"}
							</BlackButton>
						)}
						<button
							className="btn"
							onClick={() =>
								this.setState({
									showNewGroupDialog: true
								})
							}
						>
							Add New
						</button>
					</div>
				</div>

				{isLoading ? (
					<div className="section-row section-row--block">
						<GeneralLoader />
					</div>
				) : docErrors ? (
					<div className="section-row section-row--block">
						<ErrorText animate={1} errors={docErrors} />
					</div>
				) : (
					<SortableGroupList
						transitionDuration={200}
						lockAxis={"y"}
						lockToContainerEdges={true}
						lockOffset={"0%"}
						useDragHandle={true}
						helperClass="active-sort"
						groups={documents}
						changeGroupMinify={this.changeGroupMinify}
						updateLocalStateFromProps={this.updateLocalStateFromProps}
						handleTitleChange={(e, i) => this.handleTitleChange(e, i)}
						saveGroupTitle={this.saveGroupTitle}
						saveFileTitle={this.saveFileTitle}
						updateFileTitle={this.updateFileTitle}
						deleteGroup={group =>
							this.setState({
								showDeleteConfirmationDialog: true,
								deleteHandler: this.deleteGroup,
								deleteItem: group
							})
						}
						deleteFile={file =>
							this.setState({
								showDeleteConfirmationDialog: true,
								deleteHandler: this.deleteFile,
								deleteItem: file
							})
						}
						onSortEnd={({ oldIndex, newIndex }) =>
							this.onSortEndGroups(oldIndex, newIndex)
						}
						onSortEndFiles={(oldIndex, newIndex, i) =>
							this.onSortEndFiles(oldIndex, newIndex, i)
						}
					/>
				)}
				<ErrorDialog
					show={this.state.showErrorDialog}
					errors={this.state.errors}
					handleClose={() => this.setState({ showErrorDialog: false })}
				/>
				<AddNewTrainingDocumentGroupDialog
					show={this.state.showNewGroupDialog}
					inProgress={this.state.newGroupInProgress}
					handleAdd={this.addNewGroup}
					handleClose={() =>
						this.setState({
							showNewGroupDialog: false,
							addSuccess: null,
							newGroupError: null
						})
					}
					errors={this.state.newGroupError}
					success={this.state.newGroupSuccess}
				/>
				<DeleteConfirmationDialog
					show={this.state.showDeleteConfirmationDialog}
					deletionInProgress={this.state.deleteItem.deletionInProgress}
					id={this.state.deleteItem.id}
					itemTitle={this.state.deleteItem.title}
					handleDelete={this.state.deleteHandler}
					handleClose={() =>
						this.setState({
							showDeleteConfirmationDialog: false,
							deleteSuccess: null,
							deleteItem: {}
						})
					}
					deleteErrors={this.state.deleteItem.deleteErrors}
					deleteSuccess={this.state.deleteSuccess}
				/>
			</div>
		);
	}
}

const mapStateToProps = state => {
	return {
		user: state.user.admin,
		isLoading: state.trainingDocs.isLoading === undefined ? 1 : state.trainingDocs.isLoading,
		docErrors: state.trainingDocs.error === undefined ? null : state.trainingDocs.error,
		documents: state.trainingDocs.documents === undefined ? null : state.trainingDocs.documents
	};
};

export default connect(
	mapStateToProps,
	{ addRoute, getTrainingDocs, userTokenUpdate, trainingDocsSuccess }
)(AdminTrainingDocuments);



// WEBPACK FOOTER //
// ./src/components/admin/training-documents/AdminTrainingDocuments.js